/**
 * 時間帯を表す型
 */
export interface TimeRange {
  /** 開始時刻（分単位、0:00からの経過分） */
  startMinutes: number;
  /** 終了時刻（分単位、0:00からの経過分） */
  endMinutes: number;
}

/**
 * HH:mm形式の時刻を分に変換
 * @param time - "HH:mm"形式の時刻文字列
 * @returns 0:00からの経過分
 */
export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * 分をHH:mm形式に変換
 * @param minutes - 0:00からの経過分
 * @returns "HH:mm"形式の時刻文字列
 */
export const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * 時間帯配列をマージ（重複・隣接する時間帯を結合）
 * @param ranges - 時間帯配列
 * @returns マージ後の時間帯配列
 *
 * 例: [{9:00-12:00}, {11:00-14:00}] → [{9:00-14:00}]
 * 例: [{9:00-12:00}, {14:00-17:00}] → [{9:00-12:00}, {14:00-17:00}]（非連続は個別維持）
 */
export const mergeTimeRanges = (ranges: TimeRange[]): TimeRange[] => {
  if (ranges.length === 0) return [];

  // 開始時刻でソート
  const sorted = [...ranges].sort((a, b) => a.startMinutes - b.startMinutes);

  const merged: TimeRange[] = [{ ...sorted[0] }];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];

    // 重複または隣接している場合はマージ
    if (current.startMinutes <= last.endMinutes) {
      last.endMinutes = Math.max(last.endMinutes, current.endMinutes);
    } else {
      // 非連続の場合は個別のスロットとして保持
      merged.push({ ...current });
    }
  }

  return merged;
};

/**
 * 時間帯から禁止時間帯を減算
 * @param base - 元の時間帯配列
 * @param subtract - 減算する時間帯配列
 * @returns 減算後の時間帯配列
 *
 * 例: base=[{9:00-17:00}], subtract=[{12:00-13:00}]
 *     → [{9:00-12:00}, {13:00-17:00}]
 */
export const subtractTimeRanges = (
  base: TimeRange[],
  subtract: TimeRange[]
): TimeRange[] => {
  if (subtract.length === 0) return base;

  let result = base.map(r => ({ ...r }));

  for (const sub of subtract) {
    const newResult: TimeRange[] = [];

    for (const range of result) {
      // 重複なし
      if (sub.endMinutes <= range.startMinutes || sub.startMinutes >= range.endMinutes) {
        newResult.push(range);
        continue;
      }

      // 部分的に重複
      // 左側の残り
      if (range.startMinutes < sub.startMinutes) {
        newResult.push({
          startMinutes: range.startMinutes,
          endMinutes: sub.startMinutes,
        });
      }

      // 右側の残り
      if (range.endMinutes > sub.endMinutes) {
        newResult.push({
          startMinutes: sub.endMinutes,
          endMinutes: range.endMinutes,
        });
      }
    }

    result = newResult;
  }

  return result;
};
